/**
 * EmptyState — Premium empty state with icon, message, and optional CTA.
 *
 * Design: DESIGN_FUSION_MAP.md — actionable empty states with guidance.
 *
 * Uses theme tokens (not hardcoded hex) so it adapts to light/dark.
 * Pass `fullHeight` when the empty state is the page's main content, so the card
 * fills the available vertical space instead of leaving a large awkward void
 * below a small centered card (parity polish — Taylor calibration).
 */

import React, { type CSSProperties, type ReactNode } from 'react';
import { Button, css } from '@kantera/ui';

export interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  actionHref?: string;
  /** Fill the parent's height (centers the card vertically) — use for whole-page empties. */
  fullHeight?: boolean;
  /** Minimum height when not fullHeight. Default 240px so a lone card isn't a sliver. */
  minHeight?: number | string;
  style?: CSSProperties;
}

const defaultIcon = (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <circle
      cx="24"
      cy="24"
      r="20"
      stroke="var(--k-border-light)"
      strokeWidth="2"
      strokeDasharray="4 4"
    />
    <path d="M24 16v8m0 4h.01" stroke="var(--k-text-muted)" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  actionLabel,
  onAction,
  actionHref,
  fullHeight = false,
  minHeight = 240,
  style: customStyle,
}) => (
  <div
    style={css(
      {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 24px',
        textAlign: 'center',
        background: 'var(--k-bg-card)',
        border: '1px solid var(--k-border)',
        borderRadius: '14px',
        animation: 'fadeIn 300ms ease',
        // fill the page when this is the main content; otherwise a comfortable floor
        ...(fullHeight
          ? { flex: 1, minHeight: '60vh', height: '100%' }
          : { minHeight: typeof minHeight === 'number' ? `${minHeight}px` : minHeight }),
      },
      customStyle,
    )}
  >
    <div style={{ marginBottom: '16px', opacity: 0.7 }}>{icon || defaultIcon}</div>
    <p
      style={{
        fontSize: '16px',
        fontWeight: 600,
        color: 'var(--k-text-primary)',
        margin: '0 0 6px',
      }}
    >
      {title}
    </p>
    {description && (
      <p
        style={{
          fontSize: '13px',
          color: 'var(--k-text-muted)',
          margin: '0 0 20px',
          maxWidth: '320px',
          lineHeight: 1.5,
        }}
      >
        {description}
      </p>
    )}
    {actionLabel && (onAction || actionHref) && (
      <Button onClick={onAction} style={{ minWidth: '140px' }}>
        {actionLabel}
      </Button>
    )}
  </div>
);
